import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { markVideoAsWatched } from '../../store/media/videos/actions';
import NextVideo from './next-video';

const Video = ({ coverImage, duration, nextVideo, videoId, videoOptions, watched }) => {
  const videoRef = useRef(null);
  const [state, setState] = useState(
    // 'IDLE' | 'PLAYING' | 'PAUSED' | 'ENDED'
    'IDLE',
  );
  const { videoUrl } = videoOptions;
  const aspectRatio = '16:9';
  const [aspectWidth, aspectHeight] = aspectRatio.split(':');
  const isYouTube = videoUrl.includes('youtube');

  const onPlay = () => {
    window.dataLayer.push({ event: 'videoEvent', action: 'play', videoId, duration });
    watched(videoId);
    setState('PLAYING');
  };

  const onPause = () => {
    setState('PAUSED');
  };

  const onEnded = () => {
    window.dataLayer.push({ event: 'videoEvent', action: 'ended', videoId, duration });
    setState('ENDED');
  };

  useEffect(() => {
    if (isYouTube) {
      window.onYouTubeIframeAPIReady = () => {
        // eslint-disable-next-line no-unused-vars
        const player = new window.YT.Player(videoId, {
          events: {
            onReady() {
              //
            },
            onStateChange(event) {
              if (event.data === window.YT.PlayerState.ENDED) {
                onEnded();
              } else if (event.data === window.YT.PlayerState.PLAYING) {
                onPlay();
              } else if (event.data === window.YT.PlayerState.PAUSED) {
                onPause();
              }
            },
          },
        });
      };

      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(script);

      return () => {
        script.parentNode.removeChild(script);
        delete window.onYouTubeIframeAPIReady;
      };
    }
    return () => {};
  }, [isYouTube]);

  function getYouTubeEmbedUrl() {
    try {
      const url = new URL(videoUrl.startsWith('//') ? `https:${videoUrl}` : videoUrl);
      // https://developers.google.com/youtube/iframe_api_reference#Examples
      url.searchParams.set('enablejsapi', '1');
      return url.toString();
    } catch {
      return videoUrl;
    }
  }

  return (
    <div style={{ backgroundColor: '#000', position: 'relative' }}>
      {isYouTube ? (
        <iframe
          height={aspectHeight}
          id={videoId}
          src={getYouTubeEmbedUrl()}
          style={{
            aspectRatio: '16 / 9',
            backgroundColor: '#000',
            display: 'block',
            height: 'auto',
            visibility: state === 'ENDED' ? 'hidden' : 'visible',
            width: '100%',
          }}
          width={aspectWidth}
        />
      ) : (
        <video
          autoPlay
          controls
          height={aspectHeight}
          id={videoId}
          onEnded={onEnded}
          onPause={onPause}
          onPlay={onPlay}
          playsInline
          poster={coverImage}
          ref={videoRef}
          style={{
            aspectRatio: '16 / 9',
            backgroundColor: '#000',
            height: 'auto',
            width: '100%',
          }}
          width={aspectWidth}
        >
          <source src={videoUrl} type="video/mp4" />
        </video>
      )}
      {/*
       * Cover up the YouTube embed which shows thumbnails of related videos,
       * which make it hard to see our `<NextVideo/>` component
       */}
      {state === 'ENDED' && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            backgroundImage: `url(${coverImage})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: '50%',
          }}
        />
      )}
      {state === 'ENDED' && nextVideo && (
        <NextVideo nextVideo={nextVideo} onClose={() => setState('IDLE')} show />
      )}
    </div>
  );
};

Video.defaultProps = {
  duration: 0,
  nextVideo: null,
};

Video.propTypes = {
  coverImage: PropTypes.string.isRequired,
  duration: PropTypes.number,
  nextVideo: PropTypes.shape(),
  videoId: PropTypes.number.isRequired,
  videoOptions: PropTypes.shape({
    aspectRatio: PropTypes.string.isRequired,
    videoUrl: PropTypes.string.isRequired,
  }).isRequired,
  watched: PropTypes.func.isRequired,
};

export default connect(null, { watched: markVideoAsWatched })(Video);
